import { useEffect, useState } from "react";

import { type PantryItemView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { type CategoryKey, type UnitKey, pantryCopy } from "../lib/i18n/pantry";
import { PantryEditModal } from "./pantry-edit-modal";
import { ScreenShell } from "./screen-shell";
import { IconCamera, IconPlus } from "./ui/icon";
import { Eyebrow, H1, MonoMeta } from "./ui/primitives";

type Props = {
  householdId: string;
  onAddManual: () => void;
  onAddPhoto: () => void;
  onOpenRecipes: () => void;
  onOpenShopping: () => void;
};

const CAT_ORDER = [
  "nabiał",
  "mięso",
  "warzywa",
  "owoce",
  "pieczywo",
  "suche",
  "napoje",
  "mrożone",
  "słodycze",
  "przyprawy",
  "inne",
];

// Resolves the stored (Polish) category to a stable CategoryKey. Unknown
// values fall back to "inne" so grouping and display stay defined.
function categoryKey(c: string | null): CategoryKey {
  return c && CAT_ORDER.includes(c) ? (c as CategoryKey) : "inne";
}

// Localizes a stored unit to the active language; unknown units (free-form
// from AI) are shown verbatim.
function unitLabel(unit: string, t: (typeof pantryCopy)[keyof typeof pantryCopy]): string {
  return unit in t.unitLabel ? t.unitLabel[unit as UnitKey] : unit;
}

function formatQty(item: PantryItemView, t: (typeof pantryCopy)[keyof typeof pantryCopy]): string {
  if (item.quantity === null && !item.unit) return "—";
  if (item.quantity === null) return item.unit ? unitLabel(item.unit, t) : "—";
  return `${item.quantity}${item.unit ? ` ${unitLabel(item.unit, t)}` : ""}`;
}

function daysUntil(expiresAt: number): number {
  return Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000));
}

function expiryLabel(
  expiresAt: number,
  t: (typeof pantryCopy)[keyof typeof pantryCopy],
): string | null {
  const days = daysUntil(expiresAt);
  if (days < 0) return t.pantryExpired;
  if (days === 0) return t.pantryToday;
  if (days === 1) return t.pantryTomorrow;
  if (days <= 3) return t.pantryExpiryDays(days);
  return null;
}

export function PantryScreen({
  householdId,
  onAddManual,
  onAddPhoto,
  onOpenRecipes,
  onOpenShopping,
}: Props) {
  const { lang } = useLang();
  const t = pantryCopy[lang];
  const [items, setItems] = useState<PantryItemView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<PantryItemView | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listPantry(householdId)
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t.errLoad);
      });
    return () => {
      cancelled = true;
    };
  }, [householdId]);

  async function handleDelete(id: string) {
    setDeletingId(id);
    setError(null);
    try {
      await api.deletePantryItem(householdId, id);
      setItems((prev) => (prev ? prev.filter((p) => p.id !== id) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errDelete);
    } finally {
      setDeletingId(null);
    }
  }

  const groups = new Map<CategoryKey, PantryItemView[]>();
  for (const item of items ?? []) {
    const key = categoryKey(item.category);
    const bucket = groups.get(key) ?? [];
    bucket.push(item);
    groups.set(key, bucket);
  }
  const groupKeys = Array.from(groups.keys()).sort(
    (a, b) => CAT_ORDER.indexOf(a) - CAT_ORDER.indexOf(b),
  );

  const expiringSoon = (items ?? []).filter((i) => i.expiresAt && daysUntil(i.expiresAt) <= 1);

  return (
    <ScreenShell hideHeader withBottomNav>
      <header className="flex items-center justify-between" style={{ marginBottom: 22 }}>
        <MonoMeta>{t.pantryMeta}</MonoMeta>
        <button
          type="button"
          onClick={onOpenRecipes}
          className="text-[12px] font-bold"
          style={{ color: "var(--accent)" }}
        >
          {t.pantrySuggestions}
        </button>
      </header>

      <H1 accent=".">{t.pantryTitle}</H1>
      <p style={{ marginTop: 12, fontSize: 14, color: "var(--ink-2)" }}>
        <span
          className="tabular"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            color: "var(--ink)",
          }}
        >
          {t.pantryCountProducts(items?.length ?? 0)}
        </span>
        {expiringSoon[0] ? (
          <>
            {" · "}
            {t.pantryExpiringNote(
              expiringSoon[0].name.toLowerCase(),
              daysUntil(expiringSoon[0].expiresAt ?? 0) <= 0,
            )}
          </>
        ) : null}
      </p>

      {/* CTA row */}
      <div className="grid gap-2" style={{ marginTop: 20, gridTemplateColumns: "1.4fr 1fr" }}>
        <button
          type="button"
          onClick={onAddPhoto}
          className="flex items-center gap-2.5 px-3.5 py-3.5 text-left"
          style={{
            background: "var(--accent)",
            color: "var(--accent-ink)",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: "0.02em",
            boxShadow: "0 8px 22px var(--accent-glow)",
          }}
        >
          <IconCamera size={20} />
          {t.pantryCtaPhoto}
        </button>
        <button
          type="button"
          onClick={onAddManual}
          className="flex items-center gap-2 px-3.5 py-3.5"
          style={{
            background: "var(--surface)",
            color: "var(--ink)",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.02em",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <IconPlus size={20} />
          {t.pantryCtaManual}
        </button>
      </div>

      {/* secondary nav: shopping list */}
      <button
        type="button"
        onClick={onOpenShopping}
        className="mt-3 flex items-center justify-between px-3.5 py-3 text-left"
        style={{
          background: "var(--surface-2)",
          borderRadius: 10,
          color: "var(--ink)",
        }}
      >
        <span className="text-[13px] font-semibold">{t.pantryShoppingLink}</span>
        <span style={{ color: "var(--muted)" }}>→</span>
      </button>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 mt-3 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)" }}
        >
          {error}
        </p>
      ) : null}

      {items === null ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}>{t.loading}</p>
      ) : items.length === 0 ? (
        <p style={{ marginTop: 24, fontSize: 13, color: "var(--muted)" }}>{t.pantryEmpty}</p>
      ) : (
        <div className="flex flex-col" style={{ marginTop: 8 }}>
          {groupKeys.map((key) => {
            const itemsInCat = groups.get(key) ?? [];
            return (
              <section key={key} style={{ marginTop: 22 }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                  <Eyebrow>{t.categoryLabel[key].toUpperCase()}</Eyebrow>
                  <MonoMeta>{itemsInCat.length}</MonoMeta>
                </div>
                <div>
                  {itemsInCat.map((item) => {
                    const expLabel = item.expiresAt ? expiryLabel(item.expiresAt, t) : null;
                    return (
                      <div
                        key={item.id}
                        className="py-3 flex items-center gap-3"
                        style={{ borderTop: "1px solid var(--nav-border)" }}
                      >
                        <button
                          type="button"
                          onClick={() => setEditingItem(item)}
                          className="flex-1 min-w-0 text-left"
                          aria-label={t.pantryEditAria(item.name)}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="text-[14px] font-semibold truncate"
                              style={{ color: "var(--ink)" }}
                            >
                              {item.name}
                            </span>
                            {item.isStaple ? (
                              <span
                                style={{
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: "0.1em",
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  background: "var(--surface-2)",
                                  color: "var(--muted)",
                                }}
                              >
                                {t.pantryStapleTag}
                              </span>
                            ) : null}
                            {expLabel ? (
                              <span
                                style={{
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: "0.1em",
                                  padding: "2px 6px",
                                  borderRadius: 3,
                                  background: "var(--bad-bg)",
                                  color: "var(--bad-fg)",
                                }}
                              >
                                ↘ {expLabel}
                              </span>
                            ) : null}
                          </div>
                          <div style={{ marginTop: 3 }}>
                            <MonoMeta>{formatQty(item, t).toUpperCase()}</MonoMeta>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id}
                          className="shrink-0 px-1 transition-colors"
                          style={{ color: "var(--muted)", fontSize: 18 }}
                          aria-label={t.pantryDeleteAria(item.name)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <p
        style={{
          marginTop: 28,
          fontSize: 11,
          color: "var(--muted)",
          textAlign: "center",
        }}
      >
        {t.pantrySharedNote}
      </p>

      {editingItem ? (
        <PantryEditModal
          householdId={householdId}
          item={editingItem}
          onSaved={(updated) => {
            setItems((prev) => prev?.map((i) => (i.id === updated.id ? updated : i)) ?? null);
            setEditingItem(null);
          }}
          onCancel={() => setEditingItem(null)}
        />
      ) : null}
    </ScreenShell>
  );
}
